import { PaymentIntent, Transaction } from '../types/PaymentTypes';
import api from './api.interceptor';
import { API_BASE_URL } from '../config/api.config';

// Payment backend (Stripe) not implemented yet — always use mock
const USE_MOCK = true;
const PLATFORM_FEE_RATE = 0.10;

class PaymentService {
  private baseUrl = `${API_BASE_URL}/payments`;

  async createPaymentIntent(
    vacancyId: string,
    amount: number,
    currency: string
  ): Promise<PaymentIntent> {
    const totalAmount = amount + amount * PLATFORM_FEE_RATE;

    if (USE_MOCK) {
      await new Promise(r => setTimeout(r, 1000));
      return {
        id: `pi_mock_${Date.now()}`,
        client_secret: `cs_mock_${Date.now()}`,
        amount: totalAmount,
        currency,
        status: 'requires_payment_method',
      };
    }

    const response = await api.post(`${this.baseUrl}/create-intent`, {
      vacancy_id: vacancyId,
      amount: totalAmount,
      currency,
    });
    if (!response.ok) throw new Error('Failed to create payment intent');
    return await response.json();
  }

  async confirmHirePayment(
    referralId: string,
    paymentIntentId: string
  ): Promise<{ success: boolean }> {
    if (USE_MOCK) {
      await new Promise(r => setTimeout(r, 1000));
      const stored: any[] = JSON.parse(localStorage.getItem('my_referrals') || '[]');
      const idx = stored.findIndex(r => r.id === referralId);
      if (idx !== -1) {
        stored[idx].status = 'hired';
        stored[idx].payout_status = 'pending';
        localStorage.setItem('my_referrals', JSON.stringify(stored));
      }
      // Store transaction
      const transactions: Transaction[] = JSON.parse(localStorage.getItem('transactions') || '[]');
      transactions.unshift({
        id: `txn_${Date.now()}`,
        type: 'bounty_payment',
        vacancy_id: stored[idx]?.vacancy_id || '',
        referral_id: referralId,
        amount: stored[idx]?.bounty_amount || 0,
        currency: 'USD',
        status: 'completed',
        created_at: new Date().toISOString(),
      });
      localStorage.setItem('transactions', JSON.stringify(transactions));
      return { success: true };
    }

    const response = await api.post(`${this.baseUrl}/confirm-hire`, {
      referral_id: referralId,
      payment_intent_id: paymentIntentId,
    });
    if (!response.ok) throw new Error('Failed to confirm payment');
    return await response.json();
  }

  async getTransactionHistory(): Promise<Transaction[]> {
    if (USE_MOCK) {
      return JSON.parse(localStorage.getItem('transactions') || '[]');
    }

    try {
      const response = await api.get(`${this.baseUrl}/transactions`);
      if (!response.ok) return [];
      return await response.json();
    } catch {
      return [];
    }
  }

  getPlatformFee(amount: number): number {
    return Math.round(amount * PLATFORM_FEE_RATE);
  }

  getTotal(amount: number): number {
    return amount + this.getPlatformFee(amount);
  }
}

export const paymentService = new PaymentService();
export default paymentService;
